import { useEffect, useRef, useState } from "react";
import { createPortal, flushSync } from "react-dom";
import { ChevronRight, X } from "lucide-react";
import {
  formatDateOfBirthDisplay,
  partsFromDateOfBirth,
  toDateOfBirthIso,
} from "./dateOfBirthUtils";
import { useIsMobile } from "./useIsMobile";

function digitsFromIso(iso: string) {
  const parts = partsFromDateOfBirth(iso);
  return `${parts.month}${parts.day}${parts.year}`.replace(/\D/g, "").slice(0, 8);
}

function formatDateDigits(digits: string) {
  const month = digits.slice(0, 2);
  const day = digits.slice(2, 4);
  const year = digits.slice(4, 8);
  if (digits.length <= 2) return month;
  if (digits.length <= 4) return `${month}/${day}`;
  return `${month}/${day}/${year}`;
}

function isoFromDigits(digits: string) {
  if (digits.length !== 8) return null;
  const monthValue = Number.parseInt(digits.slice(0, 2), 10);
  const dayValue = Number.parseInt(digits.slice(2, 4), 10);
  const yearValue = Number.parseInt(digits.slice(4, 8), 10);
  if (Number.isNaN(monthValue) || Number.isNaN(dayValue) || Number.isNaN(yearValue)) return null;
  return toDateOfBirthIso(monthValue, dayValue, yearValue);
}

function isDigitKey(key: string) {
  return key.length === 1 && key >= "0" && key <= "9";
}

function sectionStartFromDisplayPosition(position: number) {
  if (position <= 2) return 0;
  if (position <= 5) return 2;
  return 4;
}

function digitIndexFromDisplayPosition(position: number) {
  if (position <= 2) return position;
  if (position <= 5) return position - 1;
  return position - 2;
}

function displayPositionFromDigitIndex(index: number) {
  if (index <= 1) return index;
  if (index <= 3) return index + 1;
  return index + 2;
}

function sectionStartFromPointerOffset(input: HTMLInputElement, offsetX: number) {
  const styles = window.getComputedStyle(input);
  const text = input.value || "MM/DD/YYYY";
  const textOffset = Math.max(0, offsetX - (Number.parseFloat(styles.paddingLeft) || 0));
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  if (!context) {
    const characterWidth = Math.max(1, input.clientWidth / 10);
    return sectionStartFromDisplayPosition(Math.round(textOffset / characterWidth));
  }

  context.font =
    styles.font ||
    `${styles.fontStyle} ${styles.fontVariant} ${styles.fontWeight} ${styles.fontSize} ${styles.fontFamily}`;

  const monthLimit = context.measureText(text.slice(0, 3)).width;
  const dayLimit = context.measureText(text.slice(0, 6)).width;

  if (textOffset <= monthLimit) return 0;
  if (textOffset <= dayLimit) return 2;
  return 4;
}

function DateSegmentInputs({
  month,
  day,
  year,
  onMonthChange,
  onDayChange,
  onYearChange,
  onCommit,
  className = "",
}: {
  month: string;
  day: string;
  year: string;
  onMonthChange: (value: string) => void;
  onDayChange: (value: string) => void;
  onYearChange: (value: string) => void;
  onCommit: () => void;
  className?: string;
}) {
  const monthRef = useRef<HTMLInputElement>(null);
  const dayRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);

  const distributeDigits = (digits: string) => {
    const clean = digits.replace(/\D/g, "").slice(0, 8);
    const nextMonth = clean.slice(0, 2);
    const nextDay = clean.slice(2, 4);
    const nextYear = clean.slice(4, 8);
    onMonthChange(nextMonth);
    onDayChange(nextDay);
    onYearChange(nextYear);

    if (nextDay.length === 2) {
      yearRef.current?.focus();
      yearRef.current?.select();
      return;
    }
    if (nextMonth.length === 2) {
      dayRef.current?.focus();
      dayRef.current?.select();
    }
  };

  const handleMonthChange = (next: string) => {
    const digits = next.replace(/\D/g, "");
    if (digits.length > 2) {
      distributeDigits(digits);
      return;
    }
    onMonthChange(digits);
    if (digits.length === 2) {
      dayRef.current?.focus();
      dayRef.current?.select();
    }
  };

  const handleDayChange = (next: string) => {
    const digits = next.replace(/\D/g, "");
    if (digits.length > 2) {
      distributeDigits(`${month}${digits}`);
      return;
    }
    onDayChange(digits);
    if (digits.length === 2) {
      yearRef.current?.focus();
      yearRef.current?.select();
    }
  };

  const handleYearChange = (next: string) => {
    const digits = next.replace(/\D/g, "").slice(0, 4);
    onYearChange(digits);
  };

  return (
    <div
      className={`dob-sheet-inputs ${className}`.trim()}
      onPaste={(event) => {
        const digits = event.clipboardData.getData("text").replace(/\D/g, "");
        if (digits.length < 6) return;
        event.preventDefault();
        distributeDigits(digits);
      }}
    >
      <input
        ref={monthRef}
        className="dob-sheet-segment"
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="bday-month"
        enterKeyHint="next"
        aria-label="Birth month"
        placeholder="MM"
        maxLength={2}
        value={month}
        onChange={(event) => handleMonthChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            dayRef.current?.focus();
            dayRef.current?.select();
          }
        }}
      />
      <span className="dob-sheet-separator" aria-hidden="true">/</span>
      <input
        ref={dayRef}
        className="dob-sheet-segment"
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="bday-day"
        enterKeyHint="next"
        aria-label="Birth day"
        placeholder="DD"
        maxLength={2}
        value={day}
        onChange={(event) => handleDayChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Backspace" && !day && month) {
            event.preventDefault();
            monthRef.current?.focus();
          } else if (event.key === "Enter") {
            event.preventDefault();
            yearRef.current?.focus();
            yearRef.current?.select();
          }
        }}
      />
      <span className="dob-sheet-separator" aria-hidden="true">/</span>
      <input
        ref={yearRef}
        className="dob-sheet-segment dob-sheet-segment--year"
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="bday-year"
        enterKeyHint="done"
        aria-label="Birth year"
        placeholder="YYYY"
        maxLength={4}
        value={year}
        onChange={(event) => handleYearChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Backspace" && !year && day) {
            event.preventDefault();
            dayRef.current?.focus();
          } else if (event.key === "Enter") {
            event.preventDefault();
            onCommit();
          }
        }}
      />
    </div>
  );
}

function MobileDateOfBirthSheet({
  label,
  value,
  required,
  externalError,
  onChange,
}: {
  label: string;
  value: string;
  required?: boolean;
  externalError?: string;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState("");
  const [day, setDay] = useState("");
  const [year, setYear] = useState("");
  const [error, setError] = useState("");
  const sheetRef = useRef<HTMLDivElement>(null);

  const displayValue = formatDateOfBirthDisplay(value) || "Add date";

  const resetDraft = (iso = value) => {
    const parts = partsFromDateOfBirth(iso);
    setMonth(parts.month);
    setDay(parts.day);
    setYear(parts.year);
    setError("");
  };

  const focusMonthInput = () => {
    const monthInput = sheetRef.current?.querySelector<HTMLInputElement>(
      'input[aria-label="Birth month"]',
    );
    monthInput?.focus();
    monthInput?.select();
  };

  const openSheet = () => {
    if (sheetRef.current) return;

    flushSync(() => {
      resetDraft();
      setOpen(true);
    });

    focusMonthInput();
  };

  const closeSheet = () => {
    setOpen(false);
    setError("");
  };

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeSheet();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  const saveDate = () => {
    if (month.length < 1 || day.length < 1 || year.length < 4) {
      setError("Enter your full birth month, day, and year.");
      return;
    }

    const monthValue = Number.parseInt(month, 10);
    const dayValue = Number.parseInt(day, 10);
    const yearValue = Number.parseInt(year, 10);
    const iso =
      !Number.isNaN(monthValue) && !Number.isNaN(dayValue) && !Number.isNaN(yearValue)
        ? toDateOfBirthIso(monthValue, dayValue, yearValue)
        : null;

    if (!iso) {
      setError("Enter a valid date of birth.");
      return;
    }

    onChange(iso);
    closeSheet();
  };

  const updateMonth = (next: string) => {
    setError("");
    setMonth(next);
  };

  const updateDay = (next: string) => {
    setError("");
    setDay(next);
  };

  const updateYear = (next: string) => {
    setError("");
    setYear(next);
  };

  return (
    <>
      <button
        className={[
          "app-select-field",
          "app-select-field--trigger",
          "dob-field-trigger",
          externalError ? "is-invalid" : "",
        ].filter(Boolean).join(" ")}
        type="button"
        data-profile-field="dateOfBirth"
        onClick={openSheet}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-invalid={externalError ? "true" : undefined}
        aria-required={required}
        aria-label={`${label}: ${displayValue}`}
      >
        <span className="app-select-trigger-copy">
          <span className="app-select-field-label" aria-hidden="true">
            {label}
          </span>
          <span className={`app-select-trigger-value-text ${value ? "" : "is-placeholder"}`.trim()}>
            {displayValue}
          </span>
        </span>
        <ChevronRight className="app-select-trigger-chevron" size={18} aria-hidden="true" />
      </button>
      {externalError ? (
        <span className="field-error dob-field-error" role="alert">
          {externalError}
        </span>
      ) : null}
      {open && createPortal(
        <div className="app-select-sheet-root dob-select-sheet-root" role="presentation" onClick={closeSheet}>
          <div
            ref={sheetRef}
            className="app-select-sheet dob-select-sheet"
            role="dialog"
            aria-modal="true"
            aria-label={label}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="app-select-sheet-header">
              <strong>{label}</strong>
              <button className="icon-button app-select-sheet-close" type="button" onClick={closeSheet} aria-label="Close">
                <X size={16} />
              </button>
            </div>
            <div className="dob-sheet-body">
              <p className="dob-sheet-copy">Use your birth month, day, and year.</p>
              <DateSegmentInputs
                month={month}
                day={day}
                year={year}
                onMonthChange={updateMonth}
                onDayChange={updateDay}
                onYearChange={updateYear}
                onCommit={saveDate}
              />
              {error && <p className="dob-sheet-error" role="alert">{error}</p>}
              <button className="button primary dob-sheet-save" type="button" onClick={saveDate}>
                Save date
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

function DesktopDateOfBirthField({
  label,
  value,
  onChange,
  required,
  error,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  error?: string;
}) {
  const [digits, setDigits] = useState(() => digitsFromIso(value));
  const digitsRef = useRef(digits);
  const inputRef = useRef<HTMLInputElement>(null);
  const isEditingRef = useRef(false);
  const hasEditedInSessionRef = useRef(false);
  const nextDigitIndexRef = useRef(0);
  const pointerStartedRef = useRef(false);

  useEffect(() => {
    if (isEditingRef.current) return;
    const nextDigits = digitsFromIso(value);
    digitsRef.current = nextDigits;
    setDigits(nextDigits);
  }, [value]);

  useEffect(() => {
    digitsRef.current = digits;
  }, [digits]);

  const setCaretAtDigitIndex = (index: number) => {
    window.requestAnimationFrame(() => {
      const input = inputRef.current;
      if (!input || document.activeElement !== input) return;
      const position = displayPositionFromDigitIndex(Math.min(index, 8));
      input.setSelectionRange(position, position);
    });
  };

  const commitDraft = (rawDigits: string) => {
    const nextDigits = rawDigits.replace(/\D/g, "").slice(0, 8);
    hasEditedInSessionRef.current = true;
    digitsRef.current = nextDigits;
    setDigits(nextDigits);

    const iso = isoFromDigits(nextDigits);
    if (iso) {
      onChange(iso);
      return;
    }

    if (value) onChange("");
  };

  const replaceDigitsFrom = (startIndex: number, replacement: string) => {
    const replacementDigits = replacement.replace(/\D/g, "");
    if (!replacementDigits) return;

    const start = Math.max(0, Math.min(startIndex, 8));
    const available = Math.max(0, 8 - start);
    const insertedDigits = replacementDigits.slice(0, available);
    if (!insertedDigits) return;

    const currentDigits = digitsRef.current;
    const nextDigits = `${currentDigits.slice(0, start)}${insertedDigits}${currentDigits.slice(start + insertedDigits.length)}`.slice(0, 8);
    const nextIndex = Math.min(start + insertedDigits.length, 8);
    nextDigitIndexRef.current = nextIndex;
    commitDraft(nextDigits);
    setCaretAtDigitIndex(nextIndex);
  };

  const resetSectionEdit = (startIndex: number) => {
    nextDigitIndexRef.current = startIndex;
    hasEditedInSessionRef.current = false;
  };

  const resetSectionEditFromCursor = (input: HTMLInputElement) => {
    resetSectionEdit(sectionStartFromDisplayPosition(input.selectionStart ?? 0));
  };

  const commitDigits = (nextDigits: string) => {
    isEditingRef.current = false;
    hasEditedInSessionRef.current = false;

    if (!nextDigits) {
      digitsRef.current = "";
      if (value) onChange("");
      return;
    }

    const iso = isoFromDigits(nextDigits);
    if (iso) {
      onChange(iso);
    } else if (value) {
      onChange("");
    }
  };

  return (
    <label className={`field${error ? " field--invalid" : ""}`}>
      <span>{label}</span>
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        autoComplete="bday"
        placeholder="MM/DD/YYYY"
        required={required}
        aria-label={`${label} in month, day, year format`}
        data-profile-field="dateOfBirth"
        value={formatDateDigits(digits)}
        onFocus={(event) => {
          isEditingRef.current = true;
          if (!pointerStartedRef.current) {
            resetSectionEditFromCursor(event.currentTarget);
          }
        }}
        onMouseDown={(event) => {
          pointerStartedRef.current = true;
          resetSectionEdit(sectionStartFromPointerOffset(event.currentTarget, event.nativeEvent.offsetX));
        }}
        onClick={(event) => {
          if (pointerStartedRef.current) {
            pointerStartedRef.current = false;
            return;
          }
          resetSectionEditFromCursor(event.currentTarget);
        }}
        onChange={(event) => {
          isEditingRef.current = true;
          const nextIndex = Math.max(0, Math.min(digitIndexFromDisplayPosition(event.currentTarget.selectionStart ?? 0), 8));
          commitDraft(event.target.value);
          nextDigitIndexRef.current = nextIndex;
          setCaretAtDigitIndex(nextIndex);
        }}
        onPaste={(event) => {
          const pastedDigits = event.clipboardData.getData("text").replace(/\D/g, "");
          if (!pastedDigits) return;
          event.preventDefault();
          isEditingRef.current = true;
          replaceDigitsFrom(nextDigitIndexRef.current, pastedDigits);
        }}
        onBlur={() => {
          commitDigits(digitsRef.current);
        }}
        onKeyDown={(event) => {
          if (isDigitKey(event.key)) {
            event.preventDefault();
            isEditingRef.current = true;
            replaceDigitsFrom(nextDigitIndexRef.current, event.key);
            return;
          }
          if (event.key === "Home") {
            event.preventDefault();
            resetSectionEdit(0);
            setCaretAtDigitIndex(0);
            return;
          }
          if (event.key === "End") {
            event.preventDefault();
            resetSectionEdit(4);
            setCaretAtDigitIndex(8);
            return;
          }
          if (["ArrowLeft", "ArrowRight"].includes(event.key)) {
            window.requestAnimationFrame(() => {
              const input = inputRef.current;
              if (input && document.activeElement === input) {
                resetSectionEditFromCursor(input);
              }
            });
            return;
          }
          if (event.key === "Enter") {
            event.preventDefault();
            commitDigits(digitsRef.current);
            event.currentTarget.blur();
          }
        }}
      />
      {error ? (
        <span className="field-error" role="alert">
          {error}
        </span>
      ) : null}
    </label>
  );
}

export function DateOfBirthField({
  label = "Date of birth",
  value,
  onChange,
  required,
  locked = false,
  error,
}: {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  locked?: boolean;
  error?: string;
}) {
  const mobile = useIsMobile();

  if (locked) {
    return (
      <div className="field field--locked dob-field" aria-readonly="true">
        <span>{label}</span>
        <span className="field-static-value">{formatDateOfBirthDisplay(value) || "—"}</span>
      </div>
    );
  }

  if (!mobile) {
    return (
      <DesktopDateOfBirthField
        label={label}
        value={value}
        onChange={onChange}
        required={required}
        error={error}
      />
    );
  }

  return (
    <MobileDateOfBirthSheet
      label={label}
      value={value}
      required={required}
      externalError={error}
      onChange={onChange}
    />
  );
}
